import Promise from 'bluebird';
import _ from 'underscore';
import { buildSessionExpiredUrl } from '@bingads-webui/session-expired-url';

export type AjaxTextStatus = string;
export type AjaxErrorThrown = string | undefined;
export type AjaxXhr = object | null;

// RestfulService forwards request data in this callback position.
export type AjaxSuccessContext<TData = unknown> = TData | undefined;

export type AjaxResultType = 0 | 1 | 2 | 3;
export type AjaxAlertClass = 'ErrorAlert' | 'InfoAlert' | 'SuccessAlert' | 'WarningAlert';
export type AjaxErrorCollection = readonly unknown[] | Readonly<Record<string, unknown>> | string | null;

export interface AjaxAlertOptions {
  Class: string;
  HasCloseButton?: boolean;
  HtmlContent?: string;
}

export interface AjaxAlert {
  html: string;
  allowClose?: boolean;
  cssClass: string;
  closeButtonAttributeName?: string;
}

export interface AjaxResultMetadata {
  Alert?: AjaxAlertOptions;
  Error?: string;
  Errors?: AjaxErrorCollection;
  Message?: string;
  RedirectUrl?: string;
  ResultType?: AjaxResultType;
  ValidationErrors?: AjaxErrorCollection;
}

export type AjaxResult<TResult = unknown> = TResult & AjaxResultMetadata;

export type AjaxSuccessCallback<TResult = unknown, TData = unknown> = (
  data: TResult,
  status: AjaxTextStatus,
  requestData: AjaxSuccessContext<TData>
) => void;

export type AjaxErrorCallback = (
  xhr: AjaxXhr,
  status: AjaxTextStatus,
  errorThrown: AjaxErrorThrown
) => void;

export type AjaxRedirectHandler<TResult = unknown> = (
  redirectUrl: string,
  message: AjaxAlert | string | undefined,
  data: TResult
) => void;

export interface AjaxRequestOptions<TResult = unknown, TData = unknown> {
  url: string;
  data?: TData;
  onSuccess?: AjaxSuccessCallback<TResult, TData>;
  onAjaxError?: AjaxErrorCallback;
  redirectHandler?: AjaxRedirectHandler<TResult>;
  [option: string]: unknown;
}

export interface AjaxRestService {
  get<TResult = unknown, TData = unknown>(
    uri: string,
    options: AjaxRequestOptions<TResult, TData>
  ): unknown;
  post<TResult = unknown, TData = unknown>(
    uri: string,
    data: unknown,
    options: AjaxRequestOptions<TResult, TData>
  ): unknown;
}

export interface AjaxPromiseSmartOptions {
  restService: AjaxRestService;
}

export type AjaxRequestFunction<TResult = unknown, TData = unknown> = (
  options: AjaxRequestOptions<TResult, TData>
) => unknown;

export class AjaxError extends Error {
  errorThrown: AjaxErrorThrown;
  textStatus: AjaxTextStatus;
  xhr: AjaxXhr;
  constructor(xhr: AjaxXhr, textStatus: AjaxTextStatus, errorThrown: AjaxErrorThrown) {
    super(errorThrown);
    this.name = 'AjaxError';
    this.xhr = xhr;
    this.textStatus = textStatus;
    this.errorThrown = errorThrown;
  }
}

export class JsonError<TData = unknown> extends Error {
  data: unknown;
  status: AjaxTextStatus;
  xhr: AjaxSuccessContext<TData>;
  constructor(data: unknown, status: AjaxTextStatus, xhr: AjaxSuccessContext<TData>) {
    super();
    this.name = 'JsonError';
    this.data = data;
    this.status = status;
    this.xhr = xhr;
  }
}

const errorClass = 'ErrorAlert';
const infoClass = 'InfoAlert';
const successClass = 'SuccessAlert';
const warningClass = 'WarningAlert';
const alertClassSettings: Partial<Record<AjaxAlertClass, {
  className: string;
  closeAttr?: string;
}>> = {
  [errorClass]: {
    className: 'alert-danger',
  },
  [infoClass]: {
    className: 'alert-info',
    closeAttr: 'data-closetext',
  },
  [successClass]: {
    className: 'alert-success',
    closeAttr: 'data-closetext',
  },
  [warningClass]: {
    className: 'alert-warning',
    closeAttr: 'data-ignoretext',
  },
};
const ajaxResultType = {
  Success: 0,
  Validation: 1,
  Redirect: 2,
  Alert: 3,
};

// eslint-disable-next-line consistent-return
function createAlert(options: AjaxAlertOptions | undefined): AjaxAlert | undefined {
  let settings;
  if (options && options.HtmlContent) {
    settings = alertClassSettings[options.Class as AjaxAlertClass];
    return {
      html: options.HtmlContent,
      allowClose: options.HasCloseButton,
      cssClass: `alert alert-dismissable ${settings && settings.className}`,
      closeButtonAttributeName: settings && settings.closeAttr,
    };
  }
}

function handleAjaxResult<TResult, TData>(
  data: AjaxResult<TResult> | null | undefined,
  textStatus: AjaxTextStatus,
  xhr: AjaxSuccessContext<TData>,
  successHandler: (data: AjaxResult<TResult>) => void,
  errorHandler: (
    data: AjaxResult<TResult>,
    textStatus: AjaxTextStatus,
    xhr: AjaxSuccessContext<TData>
  ) => void,
  redirectHandler: AjaxRedirectHandler<AjaxResult<TResult>> | undefined
) {
  switch (data && data.ResultType) {
    case ajaxResultType.Redirect:
      // eslint-disable-next-line no-case-declarations
      const message = createAlert(data.Alert) || data.Message;

      if (_.isFunction(redirectHandler)) {
        redirectHandler(data.RedirectUrl, message, data);
      } else {
        window.location.href = data.RedirectUrl;
      }

      return true;
    case ajaxResultType.Alert:
      if (_.isFunction(successHandler)) {
        successHandler(data);
      }

      return true;
    case ajaxResultType.Validation:
      if (!data.Errors && _.isFunction(errorHandler)) {
        errorHandler(data, textStatus, xhr);
      }

      return true;
    default:
      return false;
  }
}

function handleAjaxAuthFailure(data: AjaxResultMetadata | null | undefined) {
  if (data && data.Error && data.Error === '401') {
    window.location.href = buildSessionExpiredUrl('/Signout.aspx?reason=1&msg=sessionexpired', { source: 'ajax-promise-smart' });
    return false;
  }

  return true;
}

function ajaxSuccess<TResult, TData>(
  data: AjaxResult<TResult> | null | undefined,
  textStatus: AjaxTextStatus,
  xhr: AjaxSuccessContext<TData>,
  successHandler: (data: AjaxResult<TResult> | null | undefined) => void,
  errorHandler: (
    data: AjaxResult<TResult>,
    textStatus: AjaxTextStatus,
    xhr: AjaxSuccessContext<TData>
  ) => void,
  redirectHandler: AjaxRedirectHandler<AjaxResult<TResult>> | undefined
) {
  if (data) {
    const isKnownResultType = handleAjaxResult(data, textStatus, xhr, successHandler, errorHandler, redirectHandler);
    if (!isKnownResultType || data.ResultType === ajaxResultType.Validation) {
      if (handleAjaxAuthFailure(data)) {
        if (!_.isEmpty(data.Errors)) {
          errorHandler(data, textStatus, xhr);
        } else if (_.isEmpty(data.ValidationErrors)) {
          successHandler(data);
        } else {
          errorHandler(data, textStatus, xhr);
        }
      }
      // don't handle this case, as we will immediatly sign out the user
    }
  } else {
    successHandler(data);
  }
}

function createPromise<TResult, TData>(
  requestFunction: AjaxRequestFunction<TResult, TData>,
  options: AjaxRequestOptions<TResult, TData>
) {
  return new Promise<TResult>((resolve, reject) => {
    const stubbedOptions = _.extend({}, options);
    stubbedOptions.onSuccess = _.wrap(stubbedOptions.onSuccess, (
      fn: AjaxSuccessCallback<TResult, TData> | undefined,
      data: AjaxResult<TResult>,
      status: AjaxTextStatus,
      xhr: AjaxSuccessContext<TData>
    ) => {
      if (_.isFunction(fn)) {
        fn(data, status, xhr);
      }
      ajaxSuccess(
        data,
        status,
        xhr,
        () => resolve(data),
        () => reject(new JsonError(data, status, xhr)),
        stubbedOptions.redirectHandler
      );
    });
    stubbedOptions.onAjaxError = _.wrap(stubbedOptions.onAjaxError, (
      fn: AjaxErrorCallback | undefined,
      xhr: AjaxXhr,
      status: AjaxTextStatus,
      errorThrown: AjaxErrorThrown
    ) => {
      if (_.isFunction(fn)) {
        fn(xhr, status, errorThrown);
      }
      reject(new AjaxError(xhr, status, errorThrown));
    });

    requestFunction(stubbedOptions);
  });
}

export default class {
  restService: AjaxRestService;
  constructor({
    restService,
  }: AjaxPromiseSmartOptions) {
    this.restService = restService;
  }
  post = <TResult = unknown, TData = unknown>(options: AjaxRequestOptions<TResult, TData>) => createPromise(
    ops => this.restService.post(options.url, {}, ops),
    options
  );
  get = <TResult = unknown, TData = unknown>(options: AjaxRequestOptions<TResult, TData>) => createPromise(
    ops => this.restService.get(options.url, ops),
    options
  );
}
